/**
 * SessionTable — Active user sessions with terminate action (B.6)
 */

import { useState } from 'react';
import { useSessions, useTerminateSession } from '../../hooks/useSecurity';
import { Loader2, LogOut, Monitor, Globe, Shield } from 'lucide-react';

const ORIGIN_BADGE: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  vpn: { label: 'VPN', color: 'bg-ems-sky/10 text-ems-sky', icon: Shield },
  cloud: { label: 'Cloud', color: 'bg-ems-bg-card-hover text-ems-text-muted', icon: Globe },
  local: { label: 'Local', color: 'bg-ems-green/10 text-ems-green', icon: Monitor },
};

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function SessionTable({ isAdmin }: { isAdmin: boolean }) {
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading } = useSessions({ show_all: showAll });
  const terminateMutation = useTerminateSession();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const handleTerminate = async (sessionId: string) => {
    await terminateMutation.mutateAsync({ sessionId });
    setConfirmId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  const sessions = data?.sessions || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ems-text-primary">
          Active Sessions ({data?.total ?? 0})
        </h3>
        <label className="flex items-center gap-2 text-xs text-ems-text-muted">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded border-ems-border"
          />
          Show terminated
        </label>
      </div>

      {sessions.length === 0 ? (
        <p className="text-sm text-ems-text-muted py-4 text-center">No active sessions.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ems-text-muted border-b border-ems-border">
                <th className="pb-2 pr-3 font-medium">User</th>
                <th className="pb-2 pr-3 font-medium">IP</th>
                <th className="pb-2 pr-3 font-medium">Origin</th>
                <th className="pb-2 pr-3 font-medium">Started</th>
                <th className="pb-2 pr-3 font-medium">Last Activity</th>
                <th className="pb-2 pr-3 font-medium">Expires</th>
                {isAdmin && <th className="pb-2 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const origin = ORIGIN_BADGE[s.login_origin] || ORIGIN_BADGE.cloud;
                const isExpired = new Date(s.expires_at) < new Date();
                const isTerminated = !!s.terminated_at;
                const rowClass = isTerminated || isExpired ? 'opacity-50' : '';

                return (
                  <tr key={s.session_id} className={`border-b border-ems-border ${rowClass}`}>
                    <td className="py-2 pr-3 font-medium text-ems-text-primary">{s.email}</td>
                    <td className="py-2 pr-3 font-mono text-ems-text-muted">{s.source_ip}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${origin.color}`}>
                        <origin.icon className="w-3 h-3" />
                        {origin.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-ems-text-muted">{formatRelative(s.started_at)}</td>
                    <td className="py-2 pr-3 text-ems-text-muted">{formatRelative(s.last_activity)}</td>
                    <td className="py-2 pr-3 text-ems-text-muted">
                      {isTerminated ? (
                        <span className="text-ems-red">Terminated</span>
                      ) : isExpired ? (
                        <span className="text-ems-amber">Expired</span>
                      ) : (
                        formatRelative(s.expires_at).replace('ago', 'left').replace('just now', '<1m')
                      )}
                    </td>
                    {isAdmin && (
                      <td className="py-2">
                        {!isTerminated && !isExpired && (
                          confirmId === s.session_id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleTerminate(s.session_id)}
                                disabled={terminateMutation.isPending}
                                className="px-2 py-0.5 rounded bg-ems-red text-white text-[10px] font-medium hover:bg-ems-red/80 disabled:opacity-50"
                              >
                                {terminateMutation.isPending ? 'Ending...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setConfirmId(null)}
                                className="px-2 py-0.5 rounded bg-ems-bg-card-hover text-ems-text-muted text-[10px] font-medium hover:bg-ems-bg"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmId(s.session_id)}
                              className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-ems-red hover:bg-ems-red/5 transition-colors"
                            >
                              <LogOut className="w-3 h-3" />
                              End
                            </button>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
